// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "../components/TopNav";
import ListingFiltersBar from "../components/ListingFiltersBar";
import { getUser, setUser as persistUser } from "../auth/auth";
import { getBounties } from "../api/bounties";
import { resetBrowsingLocation, updateBrowsingLocation } from "../api/users";
import bountyPlaceholder from "../assets/bounty-placeholder.svg";
import LocationPickerModal from "../components/LocationPickerModal";
import useInfiniteScroll from "../hooks/useInfiniteScroll";
import {
  DEFAULT_BOUNTY_BROWSE_FILTERS,
  buildBountyBrowseParams,
  canClearBountyBrowseFilters,
  mergePagedItemsById,
} from "../utils/tradingBrowse";
import { formatLocationLabel, getBrowsingLocation, getBrowsingRadius } from "../utils/location";
import "../styles/Feed.css";
import "../styles/ListingShared.css";
import "../styles/LocalTrades.css";

export default function Feed() {
  const navigate = useNavigate();
  const [user, setUser] = useState(getUser());
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_BOUNTY_BROWSE_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState(DEFAULT_BOUNTY_BROWSE_FILTERS.q);
  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const requestKeyRef = useRef(0);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(filters.q);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [filters.q]);

  const { game, verifiedOnly, gradedOnly } = filters;
  const browsingLocation = useMemo(() => getBrowsingLocation(user), [user]);
  const browsingRadiusMiles = useMemo(() => getBrowsingRadius(user), [user]);

  const appliedFilters = useMemo(
    () => ({
      q: debouncedSearch,
      game,
      verifiedOnly,
      gradedOnly,
    }),
    [debouncedSearch, game, verifiedOnly, gradedOnly],
  );

  const fetchBountiesPage = useCallback(
    async (offset = 0, append = false) => {
      const requestKey = ++requestKeyRef.current;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setLoadingMore(false);
        setError("");
      }

      try {
        const params = buildBountyBrowseParams(filters, offset);
        delete params.zipCode;
        if (browsingLocation?.latitude != null && browsingLocation?.longitude != null) {
          params.lat = browsingLocation.latitude;
          params.lng = browsingLocation.longitude;
          params.radiusMiles = browsingRadiusMiles;
        }

        const response = await getBounties(params);
        if (requestKey !== requestKeyRef.current) return;

        const items = response?.items || [];
        setBounties((prev) => (append ? mergePagedItemsById(prev, items) : items));
        setHasMore(Boolean(response?.hasMore));
        setTotal(Number(response?.total || 0));
        setNextOffset(Number(response?.offset || offset) + items.length);
      } catch (fetchError) {
        if (requestKey !== requestKeyRef.current) return;
        console.error("Error fetching bounties:", fetchError);
        setError(fetchError?.message || "Could not load bounties.");
        if (!append) {
          setBounties([]);
          setHasMore(false);
          setNextOffset(0);
          setTotal(0);
        }
      } finally {
        if (requestKey === requestKeyRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [browsingLocation, browsingRadiusMiles, filters],
  );

  useEffect(() => {
    setBounties([]);
    setHasMore(false);
    setNextOffset(0);
    setTotal(0);
    void fetchBountiesPage(0, false);
  }, [fetchBountiesPage]);

  const handleLoadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    void fetchBountiesPage(nextOffset, true);
  }, [fetchBountiesPage, hasMore, loading, loadingMore, nextOffset]);

  const sentinelRef = useInfiniteScroll({
    enabled: hasMore,
    isLoading: loading || loadingMore,
    onLoadMore: handleLoadMore,
  });

  const canClear = canClearBountyBrowseFilters(filters);

  const handlePostClick = () => {
    navigate("/post-bounty");
  };

  const handleCardClick = (bounty) => {
    navigate(`/bounty-offer/${bounty.id}`);
  };

  const handleBrowsingLocationSave = async (location, radiusMiles) => {
    if (!user?.id) return;
    const updatedUser = await updateBrowsingLocation(user.id, location, radiusMiles);
    setUser(updatedUser);
    persistUser(updatedUser);
  };

  const handleBrowsingReset = async () => {
    if (!user?.id) return;
    const updatedUser = await resetBrowsingLocation(user.id, browsingRadiusMiles);
    setUser(updatedUser);
    persistUser(updatedUser);
  };

  return (
    <>
      <TopNav />
      <div className="local-trades-page shared-listing-page-shell page-with-top-nav">
        <div className="local-trades-header">
          <div>
            <h1>Local Bounties</h1>
            <p>Discover what collectors are looking for near you</p>
            {user?.id ? (
              <p style={{ marginTop: 8 }}>
                {browsingLocation ? (
                  <>
                    Browsing in {formatLocationLabel(browsingLocation)}
                    {" · "}
                    <button type="button" className="link-button" onClick={() => setLocationModalOpen(true)}>
                      Change
                    </button>
                    {user?.profileLocation ? (
                      <>
                        {" · "}
                        <button type="button" className="link-button" onClick={() => void handleBrowsingReset()}>
                          Reset to profile
                        </button>
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    Set your browsing area to see nearby bounties
                    {" · "}
                    <button type="button" className="link-button" onClick={() => setLocationModalOpen(true)}>
                      Set area
                    </button>
                  </>
                )}
              </p>
            ) : null}
          </div>
        </div>

        <div className="action-buttons">
          <button type="button" className="btn-action btn-post-trade" onClick={handlePostClick}>
            Post a Bounty
          </button>
        </div>

        <ListingFiltersBar
          mode="bounty"
          searchValue={filters.q}
          onSearchChange={(q) => setFilters((prev) => ({ ...prev, q }))}
          zipValue={filters.zipCode}
          onZipChange={(zipCode) => setFilters((prev) => ({ ...prev, zipCode }))}
          hidePostalCode
          gameValue={filters.game}
          onGameChange={(game) => setFilters((prev) => ({ ...prev, game }))}
          verifiedOnly={filters.verifiedOnly}
          onVerifiedToggle={() => setFilters((prev) => ({ ...prev, verifiedOnly: !prev.verifiedOnly }))}
          gradedOnly={filters.gradedOnly}
          onGradedToggle={() => setFilters((prev) => ({ ...prev, gradedOnly: !prev.gradedOnly }))}
          onClear={() => setFilters(DEFAULT_BOUNTY_BROWSE_FILTERS)}
          canClear={canClear}
        />

        <div className="listing-results-meta">
          {!loading && !error && (
            <p>
              Showing {bounties.length} of {total} bount{total === 1 ? "y" : "ies"}
            </p>
          )}
        </div>

        {error && <div className="feed-error">{error}</div>}

        <div className="feed-grid">
          {loading ? (
            <div className="feed-grid-loader">
              <div className="feed-loading">Loading bounties...</div>
            </div>
          ) : bounties.length === 0 ? (
            <div className="empty-state shared-listing-empty">
              <p>No bounties found</p>
              <p className="empty-subtitle">Try adjusting your filters or post a bounty of your own.</p>
            </div>
          ) : (
            <>
              {bounties.map((bounty) => {
                const isOwnBounty = user && bounty.userId === user.id;
                const matchCount = Number(bounty.matchCount || 0);

                const gradeNumber = bounty.gradeNumber ? String(bounty.gradeNumber) : "";
                const isGraded = bounty.gradingCompany && bounty.gradingCompany !== "raw" && gradeNumber;
                const isGemMint = isGraded && gradeNumber === "10";
                const bountyClassName = isGemMint
                  ? "feed-card bounty bounty-graded-gold"
                  : isGraded
                    ? "feed-card bounty bounty-graded-purple"
                    : "feed-card bounty bounty-raw";

                return (
                  <div
                    key={bounty.id}
                    className={bountyClassName}
                    onClick={() => handleCardClick(bounty)}
                  >
                    <div className="feed-card-badge">
                      Bounty
                    </div>

                    <div className="feed-card-content">
                      <img
                        src={bounty.photoUrl || bountyPlaceholder}
                        alt={bounty.cardName}
                        className="feed-card-image"
                        loading="eager"
                        decoding="async"
                        onError={(event) => {
                          event.currentTarget.src = bountyPlaceholder;
                        }}
                      />
                      <h3>{bounty.cardName}</h3>

                      <p className="feed-card-details">
                        Game: <strong>{bounty.game}</strong>
                      </p>
                      {(!bounty.gradingCompany || bounty.gradingCompany === "raw") && (
                        <p className="feed-card-details">
                          Condition: <strong>{bounty.desiredCondition || "Any"}</strong>
                        </p>
                      )}
                      {bounty.gradingCompany && bounty.gradingCompany !== "raw" && (
                        <p className="feed-card-details">
                          Grade: <strong>{bounty.gradingCompany.toUpperCase()} {bounty.gradeNumber || "Any"}</strong>
                        </p>
                      )}
                      <p className="feed-card-location">
                        Postal code {bounty.zipCode || "N/A"} • {bounty.radius || 25} mi radius
                      </p>

                      {isOwnBounty && matchCount > 0 && (
                        <p className="feed-card-match-hint">
                          {matchCount} nearby trader{matchCount > 1 ? "s" : ""} may have this card
                        </p>
                      )}

                      {isOwnBounty && matchCount === 0 && (
                        <p className="feed-card-wait">
                          Waiting for matches...
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}

              <div ref={sentinelRef} className="listing-scroll-sentinel" aria-hidden="true" />

              {loadingMore && (
                <div className="feed-grid-loader">
                  <div className="feed-loading">Loading more bounties...</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <LocationPickerModal
        isOpen={locationModalOpen}
        title="Browsing Location"
        subtitle="Choose where discovery should happen. Your posted cards stay where you set them."
        confirmLabel="Save browsing area"
        initialLocation={browsingLocation}
        initialRadiusMiles={browsingRadiusMiles}
        showRadius
        onClose={() => setLocationModalOpen(false)}
        onConfirm={handleBrowsingLocationSave}
      />
    </>
  );
}
